/* eslint-disable */
import { getAPI } from "@/axios-api";

/**
 * Fetch user subscription data
 * @returns {Promise<Object|null>} The subscription data or null if not found
 */
export const fetchUserSubscription = async () => {
  try {
    const response = await getAPI.get("subscriptions/find-subscription");
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user subscription:", error);
    return null;
  }
};

/**
 * Fetch organization data
 * @returns {Promise<Object|null>} The organization data or null if not found
 */
export const fetchOrganization = async () => {
  try {
    const response = await getAPI.get(
      `/organisations/${localStorage.getItem("organisation")}/`
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching organization:", error);
    return null;
  }
};

/**
 * Fetch users statistics
 * @returns {Promise<Object|null>} The users statistics or null if not found
 */
export const fetchUsersStats = async () => {
  try {
    const response = await getAPI.get("stat_module/statistics");
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching users stats:", error);
    return null;
  }
};

/**
 * Fetch statistics for groups/services the current user is responsible for.
 * Backend uses the auth token to identify the user and returns stats for their group(s)/service(s).
 * @returns {Promise<Object|null>} The responsible stats (e.g. { data: { groups: [], services: [] } }) or null
 */
export const fetchResponsibleStats = async () => {
  try {
    const response = await getAPI.get("stat_module/responsible_stats");
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching responsible stats:", error);
    throw error;
  }
};

/**
 * Fetch module pluzz statistics
 * @returns {Promise<Object|null>} The module pluzz statistics or null if not found
 */
export const fetchModulePluzzStats = async () => {
  try {
    const response = await getAPI.get(`start_module_pluzz/statistics`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching module pluzz statistics:", error);
    return null;
  }
};

/**
 * Fetch members data
 * @returns {Promise<Object|null>} The members data or null if not found
 */
export const fetchMembers = async () => {
  try {
    const response = await getAPI.get(
      `accounts/users/organisation/${localStorage.getItem(
        "organisation"
      )}/?is_activated=True`
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching members:", error);
    return null;
  }
};

/**
 * Fetch user progression statistics
 * @returns {Promise<Object|null>} The progression statistics or null if not found
 */
export const fetchUserProgressionStats = async () => {
  try {
    const response = await getAPI.get("accounts/invitation_statistics/");
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user progression stats:", error);
    return null;
  }
};

/**
 * Fetch organization members with progression data
 * @param {string|number} organizationId - The organization ID (optional, defaults to localStorage)
 * @returns {Promise<Array|null>} The members data or null if not found
 */
export const fetchOrganizationMembers = async (organizationId) => {
  try {
    const org = organizationId || localStorage.getItem("organisation");
    if (!org) {
      console.error("No organization ID provided");
      return null;
    }

    const response = await getAPI.get(`accounts/users/organisation/${org}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching organization members:", error);
    return null;
  }
};

/**
 * Export user progression data
 * @param {Object} exportParams - The export parameters (format, filters, etc.)
 * @returns {Promise<Blob|null>} The exported data as blob or null if failed
 */
export const exportUserProgressionData = async (exportParams) => {
  try {
    const response = await getAPI.post(
      "/users/export-progression",
      exportParams,
      {
        responseType: "blob",
      }
    );
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error exporting user progression data:", error);
    return null;
  }
};

/**
 * Fetch global statistics report grouped by entity (groups & services)
 * @returns {Promise<Object|null>} { groups: [...], services: [...] } or null
 */
export const fetchStatsByEntity = async () => {
  try {
    const response = await getAPI.get("stat_module/global_report/by_entity");
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching stats by entity:", error);
    return null;
  }
};

/**
 * Download statistical report
 * @returns {Promise<Blob|null>} The report blob or null if failed
 */
export const downloadStats = async (format, moduleType) => {
  if (format === "pdf") {
    return downloadStatsPdf(moduleType);
  }
  if (format === "excel") {
    return downloadStatsExcel(moduleType);
  }
};

export const downloadStatsPdf = async (moduleType) => {
  try {
    const endpoint =
      moduleType === "awareness"
        ? "/stat_module/download_stats"
        : moduleType === "pluzz"
        ? "/start_module_pluzz/downloading_pdf"
        : null;

    const response = await getAPI.get(endpoint, {
      responseType: "blob",
    });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error downloading stats report:", error);
    return null;
  }
};

export const downloadStatsExcel = async (moduleType) => {
  try {
    const endpoint =
      moduleType === "awareness"
        ? "/stat_module/download_stats_excel"
        : moduleType === "pluzz"
        ? "/start_module_pluzz/downloading_excel"
        : null;

    const response = await getAPI.get(endpoint, {
      responseType: "blob",
    });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error downloading stats report:", error);
    return null;
  }
};
